import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchInspections,
  fetchInspection,
  createInspection,
  patchInspectionItem,
  fetchUnitInventoryAssignments,
  uploadInspectionItemPhotos as apiUploadInspectionItemPhotos,
  deleteInspectionItemPhoto as apiDeleteInspectionItemPhoto,
  finalizeInspection as apiFinalizeInspection,
  generateInspectionProtocolPdf as apiGenerateInspectionProtocolPdf,
  fetchInspectionProtocolPdfUrl as apiFetchInspectionProtocolPdfUrl,
  signInspectionDual as apiSignInspectionDual,
  fetchInspectionSignedPdfUrl as apiFetchInspectionSignedPdfUrl,
} from "../../../api/adminData";
import { todayBusinessDate } from "../../../utils/datetime";

// Popup-safe: open a tab SYNCHRONOUSLY (before any await) so the browser does not
// treat it as a programmatic popup and block it. No "noopener,noreferrer" —
// noopener can make window.open return null and break the open-then-navigate flow.
// A small "PDF wird erstellt …" waiting page replaces the white/blank tab; writing
// it is pure UX polish wrapped in try/catch so a mobile-browser failure never
// breaks the PDF flow.
function openPdfWaitingWindow() {
  const w = window.open("", "_blank");
  if (w) {
    try {
      w.opener = null;
    } catch (_) {
      /* cross-origin / read-only opener — ignore */
    }
    try {
      w.document.write(
        `<!doctype html>
<html>
  <head>
    <title>PDF wird erstellt</title>
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body { font-family: system-ui, sans-serif; padding: 32px; color: #0f172a; }
      .box { max-width: 520px; margin: 12vh auto; line-height: 1.5; }
      .title { font-size: 20px; font-weight: 700; margin-bottom: 8px; }
      .muted { color: #64748b; }
    </style>
  </head>
  <body>
    <div class="box">
      <div class="title">PDF wird erstellt …</div>
      <div class="muted">Bitte warten, das Dokument wird vorbereitet.</div>
    </div>
  </body>
</html>`
      );
      w.document.close();
    } catch (_) {
      /* waiting page is only polish — never break the flow over it */
    }
  }
  return w;
}

export function useTenantInspections(tenantId, tenancies) {
  const [inspectionsList, setInspectionsList] = useState([]);
  const [inspectionDetailById, setInspectionDetailById] = useState({});
  const [inspectionModal, setInspectionModal] = useState(null);
  // M1c-E: "a signature just finished, for THIS tenancy". The document list
  // that goes stale lives in the tenancy card, which does not own this modal —
  // the page does — so the news travels as a named signal rather than a call.
  // The nonce only ever grows; the tenancy id is what keeps a signature in one
  // tenancy from costing document requests in every other expanded card.
  const [documentsRefreshSignal, setDocumentsRefreshSignal] = useState(null);
  const inspectionSavedItemTimeoutRef = useRef(null);

  const tenancyIdsKey = useMemo(
    () =>
      tenancies
        .map((t) => String(t?.id ?? ""))
        .filter(Boolean)
        .sort()
        .join("|"),
    [tenancies]
  );

  const reloadInspectionsCatalog = useCallback(async () => {
    try {
      const data = await fetchInspections();
      const items = Array.isArray(data?.items) ? data.items : [];
      setInspectionsList(items);
      const tidSet = new Set(tenancies.map((t) => String(t.id)));
      const relevant = items.filter((inv) => tidSet.has(String(inv.tenancy_id)));
      const details = await Promise.all(relevant.map((inv) => fetchInspection(inv.id)));
      const next = {};
      relevant.forEach((inv, i) => {
        next[inv.id] = details[i];
      });
      setInspectionDetailById(next);
    } catch {
      setInspectionsList([]);
      setInspectionDetailById({});
    }
  }, [tenancies]);

  useEffect(() => {
    if (!tenantId || !tenancyIdsKey) {
      setInspectionsList([]);
      setInspectionDetailById({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchInspections();
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setInspectionsList(items);
        const tidSet = new Set(tenancies.map((t) => String(t.id)));
        const relevant = items.filter((inv) => tidSet.has(String(inv.tenancy_id)));
        const details = await Promise.all(relevant.map((inv) => fetchInspection(inv.id)));
        if (cancelled) return;
        const next = {};
        relevant.forEach((inv, i) => {
          next[inv.id] = details[i];
        });
        setInspectionDetailById(next);
      } catch {
        if (!cancelled) {
          setInspectionsList([]);
          setInspectionDetailById({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, tenancyIdsKey, tenancies]);

  const openOrCreateInspection = async (tn, invType) => {
    if (!tn?.unit_id) {
      window.alert("Keine Einheit für dieses Mietverhältnis — Übergabe/Abnahme nicht möglich.");
      return;
    }
    const existing = inspectionsList.find(
      (x) => String(x.tenancy_id) === String(tn.id) && String(x.type) === invType
    );
    setInspectionModal({
      loading: true,
      err: null,
      tenancy: tn,
      invType,
      inspection: null,
      unitAssignments: [],
      savedItemId: null,
    });
    try {
      if (existing) {
        const [detail, unitAsg] = await Promise.all([
          fetchInspection(existing.id),
          fetchUnitInventoryAssignments(tn.unit_id),
        ]);
        setInspectionDetailById((m) => ({ ...m, [detail.id]: detail }));
        setInspectionModal({
          loading: false,
          err: null,
          tenancy: tn,
          invType,
          inspection: detail,
          unitAssignments: Array.isArray(unitAsg) ? unitAsg : [],
          savingItemId: null,
          savedItemId: null,
        });
        return;
      }
      let created;
      try {
        created = await createInspection({
          tenancy_id: String(tn.id),
          unit_id: String(tn.unit_id),
          type: invType,
          inspection_date: todayBusinessDate(),
          // Target room comes from the tenancy itself; backend re-derives and
          // validates it. Omit when absent (legacy tenancy) for graceful fallback.
          ...(tn.room_id ? { room_id: String(tn.room_id) } : {}),
        });
      } catch (e) {
        const msg = String(e?.message || "");
        if (/already exists|409/i.test(msg)) {
          const data = await fetchInspections({
            tenancy_id: String(tn.id),
            type: invType,
          }).catch(() => ({ items: [] }));
          const items = Array.isArray(data?.items) ? data.items : [];
          const row = items[0];
          if (row) {
            const [detail, unitAsg] = await Promise.all([
              fetchInspection(row.id),
              fetchUnitInventoryAssignments(tn.unit_id),
            ]);
            setInspectionsList((prev) => {
              const has = prev.some((x) => String(x.id) === String(row.id));
              return has ? prev : [...prev, row];
            });
            setInspectionDetailById((m) => ({ ...m, [detail.id]: detail }));
            setInspectionModal({
              loading: false,
              err: null,
              tenancy: tn,
              invType,
              inspection: detail,
              unitAssignments: Array.isArray(unitAsg) ? unitAsg : [],
              savingItemId: null,
              savedItemId: null,
            });
            return;
          }
        }
        throw e;
      }
      setInspectionsList((prev) => [...prev, created]);
      setInspectionDetailById((m) => ({ ...m, [created.id]: created }));
      const unitAsg = await fetchUnitInventoryAssignments(tn.unit_id).catch(() => []);
      setInspectionModal({
        loading: false,
        err: null,
        tenancy: tn,
        invType,
        inspection: created,
        unitAssignments: Array.isArray(unitAsg) ? unitAsg : [],
        savingItemId: null,
        savedItemId: null,
      });
    } catch (e) {
      setInspectionModal({
        loading: false,
        err: e?.message || "Inspection fehlgeschlagen.",
        tenancy: tn,
        invType,
        inspection: null,
        unitAssignments: [],
        savingItemId: null,
        savedItemId: null,
      });
    }
  };

  const saveInspectionItemRow = async (itemId, checkStatus, conditionNote) => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return;
    setInspectionModal((prev) => (prev ? { ...prev, savingItemId: itemId } : prev));
    try {
      await patchInspectionItem(m.inspection.id, itemId, {
        check_status: checkStatus,
        condition_note: conditionNote && String(conditionNote).trim() ? String(conditionNote).trim() : null,
      });
      const detail = await fetchInspection(m.inspection.id);
      setInspectionDetailById((prev) => ({ ...prev, [detail.id]: detail }));
      if (inspectionSavedItemTimeoutRef.current) {
        clearTimeout(inspectionSavedItemTimeoutRef.current);
        inspectionSavedItemTimeoutRef.current = null;
      }
      setInspectionModal((prev) =>
        prev && prev.inspection
          ? { ...prev, inspection: detail, savingItemId: null, savedItemId: itemId }
          : prev
      );
      inspectionSavedItemTimeoutRef.current = setTimeout(() => {
        inspectionSavedItemTimeoutRef.current = null;
        setInspectionModal((prev) => (prev ? { ...prev, savedItemId: null } : prev));
      }, 1500);
      setInspectionsList((prev) => prev.map((x) => (String(x.id) === String(detail.id) ? { ...x, ...detail } : x)));
    } catch (e) {
      window.alert(e?.message || "Speichern fehlgeschlagen.");
      setInspectionModal((prev) => (prev ? { ...prev, savingItemId: null } : prev));
    }
  };

  const uploadInspectionItemPhotos = async (itemId, files) => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return;
    const list = Array.from(files || []);
    if (list.length === 0) return;
    setInspectionModal((prev) =>
      prev ? { ...prev, uploadingPhotoItemId: itemId } : prev
    );
    try {
      await apiUploadInspectionItemPhotos(m.inspection.id, itemId, list);
      const detail = await fetchInspection(m.inspection.id);
      setInspectionDetailById((prev) => ({ ...prev, [detail.id]: detail }));
      setInspectionModal((prev) =>
        prev && prev.inspection
          ? { ...prev, inspection: detail, uploadingPhotoItemId: null }
          : prev
      );
    } catch (e) {
      window.alert(e?.message || "Foto-Upload fehlgeschlagen.");
      setInspectionModal((prev) =>
        prev ? { ...prev, uploadingPhotoItemId: null } : prev
      );
    }
  };

  const deleteInspectionItemPhoto = async (_itemId, photoId) => {
    const m = inspectionModal;
    if (!m?.inspection?.id || !photoId) return;
    try {
      await apiDeleteInspectionItemPhoto(photoId);
      const detail = await fetchInspection(m.inspection.id);
      setInspectionDetailById((prev) => ({ ...prev, [detail.id]: detail }));
      setInspectionModal((prev) =>
        prev && prev.inspection ? { ...prev, inspection: detail } : prev
      );
    } catch (e) {
      window.alert(e?.message || "Foto löschen fehlgeschlagen.");
    }
  };

  const finalizeInspection = async (finalizedNote) => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return;
    if (m.inspection.finalized_at) return; // already finalized — no-op
    setInspectionModal((prev) => (prev ? { ...prev, finalizing: true } : prev));
    try {
      const detail = await apiFinalizeInspection(m.inspection.id, finalizedNote);
      setInspectionDetailById((prev) => ({ ...prev, [detail.id]: detail }));
      setInspectionModal((prev) =>
        prev && prev.inspection
          ? { ...prev, inspection: detail, finalizing: false }
          : prev
      );
      setInspectionsList((prev) =>
        prev.map((x) => (String(x.id) === String(detail.id) ? { ...x, ...detail } : x))
      );
    } catch (e) {
      window.alert(e?.message || "Übergabe abschließen fehlgeschlagen.");
      setInspectionModal((prev) => (prev ? { ...prev, finalizing: false } : prev));
    }
  };

  const generateProtocolPdf = async (force = false) => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return;
    if (!m.inspection.finalized_at) return; // PDF only after finalized
    setInspectionModal((prev) => (prev ? { ...prev, generatingProtocolPdf: true } : prev));
    try {
      const result = await apiGenerateInspectionProtocolPdf(m.inspection.id, { force });
      setInspectionModal((prev) => {
        if (!prev || !prev.inspection) return prev;
        const updated = {
          ...prev.inspection,
          protocol_pdf_object_key:
            result?.object_key ?? prev.inspection.protocol_pdf_object_key,
          protocol_pdf_generated_at:
            result?.generated_at ?? prev.inspection.protocol_pdf_generated_at,
          protocol_pdf_size_bytes:
            result?.size_bytes ?? prev.inspection.protocol_pdf_size_bytes,
          protocol_pdf_generated_by:
            result?.generated_by ?? prev.inspection.protocol_pdf_generated_by,
        };
        return { ...prev, inspection: updated, generatingProtocolPdf: false };
      });
      setInspectionDetailById((prev) => {
        const cur = prev[m.inspection.id];
        if (!cur) return prev;
        return {
          ...prev,
          [m.inspection.id]: {
            ...cur,
            protocol_pdf_object_key: result?.object_key ?? cur.protocol_pdf_object_key,
            protocol_pdf_generated_at: result?.generated_at ?? cur.protocol_pdf_generated_at,
            protocol_pdf_size_bytes: result?.size_bytes ?? cur.protocol_pdf_size_bytes,
            protocol_pdf_generated_by: result?.generated_by ?? cur.protocol_pdf_generated_by,
          },
        };
      });
    } catch (e) {
      window.alert(e?.message || "PDF konnte nicht generiert werden.");
      setInspectionModal((prev) => (prev ? { ...prev, generatingProtocolPdf: false } : prev));
    }
  };

  // Popup-safe: open the tab synchronously BEFORE awaiting the URL, then navigate.
  const openProtocolPdf = async () => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return;
    const pdfWindow = openPdfWaitingWindow();
    setInspectionModal((prev) => (prev ? { ...prev, openingProtocolPdf: true } : prev));
    try {
      const url = await apiFetchInspectionProtocolPdfUrl(m.inspection.id);
      if (url && pdfWindow) {
        pdfWindow.location = url;
      } else if (!pdfWindow) {
        window.alert("Popup wurde blockiert. Bitte Popups erlauben.");
      } else {
        pdfWindow.close();
        window.alert("Es wurde keine PDF-URL zurückgegeben.");
      }
    } catch (e) {
      pdfWindow?.close();
      window.alert(e?.message || "PDF konnte nicht geöffnet werden.");
    } finally {
      setInspectionModal((prev) => (prev ? { ...prev, openingProtocolPdf: false } : prev));
    }
  };

  // Dual-sign + open the signed PDF. Popup-safe: opens the waiting tab
  // SYNCHRONOUSLY as the very first step (no await before it), then awaits. The
  // signature_place travels in the sign-dual payload — NO patchInspection (the
  // backend locks normal PATCH after finalize). Returns true on a successful sign
  // so the modal can close its panel. signature_place is captured from the modal.
  const signInspectionDualAndOpenPdf = async (payload) => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return false;
    const pdfWindow = openPdfWaitingWindow();
    setInspectionModal((prev) => (prev ? { ...prev, signing: true } : prev));
    try {
      await apiSignInspectionDual(m.inspection.id, payload);
      // The signature is committed, and with it the tenancy-document projection
      // the backend wrote in the same transaction. Signalled HERE, before the
      // best-effort steps below, because none of them can make the signature
      // less real — and a failed PDF open must not cost the document list its
      // update. Exactly once per successful sign.
      const signedTenancyId = m?.tenancy?.id ?? m?.inspection?.tenancy_id ?? null;
      if (signedTenancyId != null) {
        setDocumentsRefreshSignal((prev) => ({
          tenancyId: String(signedTenancyId),
          nonce: (prev?.nonce ?? 0) + 1,
        }));
      }
      // Sign succeeded; opening the signed PDF is best-effort and must not mask it.
      try {
        const url = await apiFetchInspectionSignedPdfUrl(m.inspection.id);
        if (url && pdfWindow) pdfWindow.location = url;
        else if (!pdfWindow) window.alert("Popup wurde blockiert. Bitte Popups erlauben.");
        else pdfWindow.close();
      } catch (e2) {
        pdfWindow?.close();
        window.alert(e2?.message || "Unterschriebenes PDF konnte nicht geöffnet werden.");
      }
      const detail = await fetchInspection(m.inspection.id);
      setInspectionDetailById((prev) => ({ ...prev, [detail.id]: detail }));
      setInspectionModal((prev) =>
        prev && prev.inspection ? { ...prev, inspection: detail } : prev
      );
      setInspectionsList((prev) =>
        prev.map((x) => (String(x.id) === String(detail.id) ? { ...x, ...detail } : x))
      );
      return true;
    } catch (e) {
      pdfWindow?.close();
      window.alert(e?.message || "Signatur konnte nicht gespeichert werden.");
      return false;
    } finally {
      setInspectionModal((prev) => (prev ? { ...prev, signing: false } : prev));
    }
  };

  // Popup-safe open of the already-signed PDF.
  const openSignedInspectionPdf = async () => {
    const m = inspectionModal;
    if (!m?.inspection?.id) return;
    const pdfWindow = openPdfWaitingWindow();
    setInspectionModal((prev) => (prev ? { ...prev, openingSignedPdf: true } : prev));
    try {
      const url = await apiFetchInspectionSignedPdfUrl(m.inspection.id);
      if (url && pdfWindow) {
        pdfWindow.location = url;
      } else if (!pdfWindow) {
        window.alert("Popup wurde blockiert. Bitte Popups erlauben.");
      } else {
        pdfWindow.close();
        window.alert("Unterschriebenes PDF ist noch nicht verfügbar.");
      }
    } catch (e) {
      pdfWindow?.close();
      window.alert(e?.message || "Unterschriebenes PDF konnte nicht geöffnet werden.");
    } finally {
      setInspectionModal((prev) => (prev ? { ...prev, openingSignedPdf: false } : prev));
    }
  };

  const closeInspectionModal = () => {
    if (inspectionSavedItemTimeoutRef.current) {
      clearTimeout(inspectionSavedItemTimeoutRef.current);
      inspectionSavedItemTimeoutRef.current = null;
    }
    setInspectionModal(null);
    void reloadInspectionsCatalog();
  };

  return {
    inspectionsList,
    inspectionDetailById,
    inspectionModal,
    openOrCreateInspection,
    saveInspectionItemRow,
    uploadInspectionItemPhotos,
    deleteInspectionItemPhoto,
    finalizeInspection,
    generateProtocolPdf,
    openProtocolPdf,
    signInspectionDualAndOpenPdf,
    openSignedInspectionPdf,
    closeInspectionModal,
    documentsRefreshSignal,
  };
}
